/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Talking Head Avatar & AI Lip-Sync Studio View (Task 10.4.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef, useState } from 'react';
import {
  UserCheck,
  Video,
  Mic,
  Volume2,
  UploadCloud,
  Check,
  AlertCircle,
  Copy,
  Trash2,
  RotateCcw,
  Zap,
  Layers,
  Film,
  Building,
  Monitor,
  X,
  Play,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import {
  useTalkingAvatarStore,
  AVATAR_BASE_COST,
  AVATAR_PRESENTERS,
  AVATAR_VOICES,
  SAMPLE_SCRIPTS,
  AvatarBackgroundPreset,
  AvatarAspectRatio,
  AvatarModelId,
} from '../stores/useTalkingAvatarStore';
import { CustomVideoPlayer } from '../components/media/CustomVideoPlayer';

interface TalkingAvatarStudioViewProps {
  onNavigateToVideo?: () => void;
  onNavigateToGallery?: () => void;
}

export const TalkingAvatarStudioView: React.FC<TalkingAvatarStudioViewProps> = ({
  onNavigateToVideo,
  onNavigateToGallery,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    selectedAvatarId,
    customAvatarUrl,
    script,
    selectedVoiceId,
    background,
    customBackgroundUrl,
    model,
    aspectRatio,
    title,
    isPlayingVoiceSample,
    isGenerating,
    generationProgress,
    generationStage,
    errorMessage,
    activeVideo,
    avatarVideos,
    setSelectedAvatarId,
    setCustomAvatarUrl,
    setScript,
    setSelectedVoiceId,
    setBackground,
    setCustomBackgroundUrl,
    setModel,
    setAspectRatio,
    setTitle,
    setActiveVideo,
    applySampleScript,
    playVoicePreview,
    generateAvatarVideo,
    deleteAvatarVideo,
    resetStudio,
  } = useTalkingAvatarStore();

  const [copiedScript, setCopiedScript] = useState<boolean>(false);
  const [showSampleScripts, setShowSampleScripts] = useState<boolean>(true);

  // Handle local portrait photo upload for custom avatar lip-sync
  const handlePortraitUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        setCustomAvatarUrl(result);
        setSelectedAvatarId('custom');
      }
    };
    reader.readAsDataURL(file);
  };

  // Copy active video script to clipboard
  const handleCopyScript = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedScript(true);
    setTimeout(() => setCopiedScript(false), 2000);
  };

  // Background setting cards
  const backdropOptions: { id: AvatarBackgroundPreset; labelKey: string; icon: React.FC<{ className?: string }> }[] = [
    { id: 'transparent', labelKey: 'avatar.bgTransparent', icon: Layers },
    { id: 'green_screen', labelKey: 'avatar.bgGreenScreen', icon: Monitor },
    { id: 'office', labelKey: 'avatar.bgOffice', icon: Building },
    { id: 'studio', labelKey: 'avatar.bgStudio', icon: Film },
    { id: 'custom', labelKey: 'avatar.bgCustom', icon: UploadCloud },
  ];

  return (
    <div className="space-y-6">
      {/* 1. Header Banner & Mode Jump */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 sm:p-6 rounded-2xl border border-surface-border bg-surface shadow-glass">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-4 h-4" />
            </div>
            <h2 className="text-xl sm:text-2xl font-heading font-extrabold text-foreground tracking-tight">
              {t('avatar.title')}
            </h2>
          </div>
          <p className="text-foreground-muted text-xs sm:text-sm max-w-2xl">
            {t('avatar.subtitle')}
          </p>
        </div>

        {/* Header Right Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {onNavigateToGallery && (
            <button
              onClick={onNavigateToGallery}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted/60 text-xs font-semibold text-foreground transition-all"
            >
              <Layers className="w-3.5 h-3.5 text-primary" />
              <span>{t('gallery.title')}</span>
            </button>
          )}

          {onNavigateToVideo && (
            <button
              onClick={onNavigateToVideo}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted/60 text-xs font-semibold text-foreground transition-all"
            >
              <Video className="w-3.5 h-3.5 text-primary" />
              <span>{t('video.title')}</span>
            </button>
          )}

          <button
            type="button"
            onClick={resetStudio}
            className="p-2.5 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted/60 text-foreground-muted hover:text-foreground transition-colors"
            title="Reset studio parameters"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 2. Error Message Banner */}
      {errorMessage && (
        <div className="flex items-start gap-3 p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-400 text-sm animate-in fade-in">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 font-medium">{errorMessage}</div>
          <button
            onClick={() => useTalkingAvatarStore.setState({ errorMessage: null })}
            className="p-1 rounded-lg hover:bg-rose-500/20 text-rose-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 3. Main Two-Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Creator Configuration (7 cols on lg) */}
        <div className="lg:col-span-7 space-y-6">
          {/* Card A: Presenter Avatar Catalog */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4 shadow-glass">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-sm sm:text-base text-foreground">
                  {t('avatar.catalogTitle')}
                </h3>
                <p className="text-foreground-muted text-xs">
                  {t('avatar.catalogSubtitle')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-surface-border bg-surface-elevated hover:bg-muted/60 text-xs font-semibold text-foreground transition-colors"
              >
                <UploadCloud className="w-3.5 h-3.5 text-primary" />
                <span>{t('avatar.uploadCustomPortrait')}</span>
              </button>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handlePortraitUpload}
              className="hidden"
            />

            {/* Custom portrait preview if uploaded */}
            {customAvatarUrl && (
              <div className="p-3 rounded-xl border border-primary/40 bg-primary/10 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <img
                    src={customAvatarUrl}
                    alt="Custom portrait"
                    className="w-12 h-12 rounded-lg object-cover border border-primary/50"
                  />
                  <div>
                    <span className="text-xs font-bold text-foreground">
                      {t('avatar.customAvatarTab')}
                    </span>
                    <p className="text-[11px] text-foreground-muted">
                      {t('avatar.uploadPortraitHint')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setCustomAvatarUrl(null);
                    setSelectedAvatarId('anna_news');
                  }}
                  className="p-1 rounded-lg hover:bg-rose-500/20 text-rose-400"
                  title="Remove custom portrait"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Avatars Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {AVATAR_PRESENTERS.map((p) => {
                const isSelected = selectedAvatarId === p.id && !customAvatarUrl;
                return (
                  <div
                    key={p.id}
                    onClick={() => {
                      setCustomAvatarUrl(null);
                      setSelectedAvatarId(p.id);
                    }}
                    className={`p-3 rounded-xl border cursor-pointer transition-all flex flex-col items-center text-center space-y-2 group ${
                      isSelected
                        ? 'border-primary bg-primary/10 shadow-glow-primary/20 ring-1 ring-primary/40'
                        : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated hover:border-surface-border/80'
                    }`}
                  >
                    <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-2 transition-transform duration-300 group-hover:scale-105 border-surface-border">
                      <img
                        src={p.avatarUrl}
                        alt={p.name}
                        className="w-full h-full object-cover"
                      />
                      {isSelected && (
                        <div className="absolute inset-0 bg-primary/30 flex items-center justify-center">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>

                    <div className="space-y-0.5 w-full">
                      <span className="text-xs font-bold text-foreground block truncate">
                        {p.name}
                      </span>
                      <span className="text-[10px] font-medium text-foreground-muted block truncate">
                        {t(p.roleKey as any)}
                      </span>
                      <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-muted/60 text-foreground-muted inline-block">
                        {p.nationality}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Card B: Speech Script & Dialogue */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4 shadow-glass">
            <div className="flex items-center justify-between">
              <label className="font-heading font-bold text-sm sm:text-base text-foreground">
                {t('avatar.scriptTitle')}
              </label>
              <span className="text-xs font-mono text-foreground-muted">
                {t('avatar.scriptCharCount', { count: script.length })}
              </span>
            </div>

            <textarea
              rows={4}
              value={script}
              maxLength={5000}
              onChange={(e) => setScript(e.target.value)}
              placeholder={t('avatar.scriptPlaceholder')}
              className="w-full px-3.5 py-2.5 rounded-xl border border-surface-border bg-surface-elevated text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all resize-y"
            />

            {/* Sample Scripts Inspiration */}
            <div className="space-y-2 pt-1 border-t border-surface-border/60">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-foreground-muted">
                  {t('avatar.sampleScriptsTitle')}
                </span>
                <button
                  type="button"
                  onClick={() => setShowSampleScripts(!showSampleScripts)}
                  className="text-[11px] text-primary hover:underline"
                >
                  {showSampleScripts ? 'Hide' : 'Show'}
                </button>
              </div>

              {showSampleScripts && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {SAMPLE_SCRIPTS.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => applySampleScript(s.script)}
                      className="p-2.5 rounded-xl border border-surface-border bg-surface-elevated/40 hover:bg-surface-elevated hover:border-primary/40 transition-all text-left space-y-1 group"
                    >
                      <span className="text-xs font-bold text-foreground group-hover:text-primary transition-colors block truncate">
                        {s.title}
                      </span>
                      <p className="text-[10px] text-foreground-muted line-clamp-2">
                        {s.script}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Card C: AI Voice & Audio Audition */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-3 shadow-glass">
            <div className="flex items-center justify-between">
              <label className="font-heading font-bold text-sm sm:text-base text-foreground">
                {t('avatar.voiceSelectorTitle')}
              </label>
              <button
                type="button"
                onClick={playVoicePreview}
                disabled={isPlayingVoiceSample}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold bg-accent/15 text-accent border border-accent/25 hover:bg-accent/25 transition-colors disabled:opacity-50"
              >
                {isPlayingVoiceSample ? (
                  <>
                    <Volume2 className="w-3.5 h-3.5 animate-pulse" />
                    <span>{t('avatar.playingPreview')}</span>
                  </>
                ) : (
                  <>
                    <Play className="w-3.5 h-3.5 fill-current" />
                    <span>{t('avatar.previewVoiceBtn')}</span>
                  </>
                )}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {AVATAR_VOICES.map((v) => {
                const isSelected = selectedVoiceId === v.id;
                return (
                  <div
                    key={v.id}
                    onClick={() => setSelectedVoiceId(v.id)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between gap-2 ${
                      isSelected
                        ? 'border-primary bg-primary/10 ring-1 ring-primary/40'
                        : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <div className="w-7 h-7 rounded-lg bg-surface-border flex items-center justify-center flex-shrink-0">
                        <Mic className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <div className="truncate">
                        <span className="text-xs font-bold text-foreground block truncate">
                          {v.name}
                        </span>
                        <span className="text-[10px] text-foreground-muted font-mono">
                          {v.language} · {v.provider}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Card D: Studio Backdrop & Setting */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-3 shadow-glass">
            <label className="font-heading font-bold text-sm sm:text-base text-foreground">
              {t('avatar.backgroundTitle')}
            </label>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {backdropOptions.map((opt) => {
                const Icon = opt.icon;
                const isSelected = background === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setBackground(opt.id)}
                    className={`p-2.5 rounded-xl border text-xs font-medium flex items-center gap-2 transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-primary font-bold ring-1 ring-primary/40'
                        : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{t(opt.labelKey as any)}</span>
                  </button>
                );
              })}
            </div>

            {background === 'custom' && (
              <input
                type="url"
                value={customBackgroundUrl}
                onChange={(e) => setCustomBackgroundUrl(e.target.value)}
                placeholder={t('avatar.customBgUrlPlaceholder')}
                className="w-full px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all mt-2"
              />
            )}
          </div>

          {/* Card E: Engine Model, Aspect Ratio & Title */}
          <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4 shadow-glass">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Engine Model */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground-muted">
                  {t('avatar.modelTitle')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['synthesia', 'kling', 'modelslab'] as AvatarModelId[]).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setModel(m)}
                      className={`p-2 rounded-xl border text-xs font-medium capitalize transition-all ${
                        model === m
                          ? 'border-primary bg-primary/10 text-primary font-bold ring-1 ring-primary/40'
                          : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated text-foreground-muted hover:text-foreground'
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              {/* Aspect Ratio */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-foreground-muted">
                  {t('avatar.aspectRatioTitle')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['16:9', '9:16', '1:1'] as AvatarAspectRatio[]).map((ratio) => (
                    <button
                      key={ratio}
                      type="button"
                      onClick={() => setAspectRatio(ratio)}
                      className={`p-2 rounded-xl border text-xs font-medium transition-all ${
                        aspectRatio === ratio
                          ? 'border-primary bg-primary/10 text-primary font-bold ring-1 ring-primary/40'
                          : 'border-surface-border bg-surface-elevated/60 hover:bg-surface-elevated text-foreground-muted hover:text-foreground'
                      }`}
                    >
                      {ratio}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Video Title */}
            <div className="space-y-1 pt-1">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Video Title (Optional)"
                className="w-full px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all"
              />
            </div>
          </div>

          {/* Action Row: Generate Button & Progress */}
          <div className="space-y-3">
            <button
              onClick={() => generateAvatarVideo()}
              disabled={isGenerating || !script.trim()}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl bg-primary hover:bg-primary/90 text-primary-fg font-heading font-bold text-sm sm:text-base shadow-glow-primary/30 transition-all disabled:opacity-50 active:scale-[0.99]"
            >
              <Zap className="w-5 h-5 fill-current" />
              <span>
                {isGenerating
                  ? t('avatar.generatingVideo')
                  : t('avatar.generateVideoBtn')}
              </span>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white ml-2">
                {t('avatar.costBadge', { credits: AVATAR_BASE_COST })}
              </span>
            </button>

            {isGenerating && (
              <div className="p-4 rounded-xl border border-surface-border bg-surface space-y-2 animate-in fade-in">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-muted truncate">
                    {generationStage}
                  </span>
                  <span className="font-mono font-bold text-primary">
                    {generationProgress}%
                  </span>
                </div>
                <div className="w-full h-2 bg-surface-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300"
                    style={{ width: `${generationProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Featured Player & Video History (5 cols on lg) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Active Featured Video Player */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-sm sm:text-base text-foreground">
                {t('avatar.featuredPlayerTitle')}
              </h3>
              {activeVideo && (
                <button
                  onClick={() => handleCopyScript(activeVideo.script)}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  {copiedScript ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>{t('avatar.scriptCopied')}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>{t('avatar.copyScript')}</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {activeVideo ? (
              <div className="space-y-3">
                <CustomVideoPlayer video={activeVideo} />

                {/* Metadata & Script Card */}
                <div className="p-4 rounded-xl border border-surface-border bg-surface space-y-3 shadow-glass">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-foreground truncate">
                      {activeVideo.title || activeVideo.presenterName}
                    </span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                      {activeVideo.model}
                    </span>
                  </div>

                  <p className="text-xs text-foreground-muted leading-relaxed line-clamp-4">
                    "{activeVideo.script}"
                  </p>

                  <div className="flex items-center justify-between pt-2 border-t border-surface-border/60 text-[11px] text-foreground-muted">
                    <span className="font-mono">
                      {t('avatar.presenterBadge', { name: activeVideo.presenterName })}
                    </span>
                    <button
                      onClick={() => deleteAvatarVideo(activeVideo.id)}
                      className="text-rose-400 hover:text-rose-300 flex items-center gap-1 transition-colors"
                      title="Delete video from history"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 rounded-2xl border border-dashed border-surface-border bg-surface flex flex-col items-center justify-center text-center gap-3">
                <UserCheck className="w-10 h-10 text-foreground-muted opacity-40" />
                <div className="space-y-1">
                  <span className="text-sm font-semibold text-foreground">
                    {t('avatar.noAvatarVideosYet')}
                  </span>
                  <p className="text-xs text-foreground-muted max-w-xs">
                    {t('avatar.noAvatarVideosDesc')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Recent Avatar Videos Feed */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-sm sm:text-base text-foreground">
                {t('avatar.recentAvatarVideosTitle')}
              </h3>
              <span className="text-xs text-foreground-muted font-mono">
                {avatarVideos.length} clips
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {avatarVideos.map((item) => {
                const isActive = activeVideo?.id === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => setActiveVideo(item)}
                    className={`relative aspect-video rounded-xl overflow-hidden border cursor-pointer group transition-all ${
                      isActive
                        ? 'border-primary ring-2 ring-primary/50 shadow-glow-primary/30'
                        : 'border-surface-border hover:border-surface-border/80'
                    }`}
                  >
                    <img
                      src={item.thumbnailUrl}
                      alt={item.title || item.script}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />

                    {/* Play Badge Overlay */}
                    <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur-sm group-hover:scale-110 transition-transform">
                        <Play className="w-3.5 h-3.5 ml-0.5 fill-current" />
                      </div>
                    </div>

                    {/* Duration badge */}
                    <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold bg-black/70 text-white backdrop-blur-sm">
                      {item.durationSeconds}s
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
